import { z } from 'zod';
import { rewardStatusEnum, redemptionStatusEnum, spendPeriodEnum, spendTierEnum } from '../enums';
import { countryCodeSchema, isoDateTimeSchema, paginationQuerySchema, slugSchema, urlSchema, uuidSchema } from './common';

// Points & Rewards (loyalty). Concept admins set per-garage spend targets per
// tier (normal/digital); points accrue on all spend at the tier's rate but only
// unlock once the garage reaches that tier's target. Rewards are a single global
// catalogue (platform-admin managed) tagged with concept + country visibility.

// One tier's target — amount 0 means "no target set" (everything pending).
export const spendTargetSchema = z.object({
  tier: spendTierEnum,
  targetAmount: z.number().nonnegative(),
  pointsPerPound: z.number().nonnegative(),
  period: spendPeriodEnum,
});
export type SpendTargetPayload = z.infer<typeof spendTargetSchema>;

// Concept admin sets both tiers for a garage in one call.
export const setSpendTargetsSchema = z.object({
  targets: z.array(spendTargetSchema).max(2),
});
export type SetSpendTargetsPayload = z.infer<typeof setSpendTargetsSchema>;

// Global reward (platform admin). Empty conceptIds = all concepts; empty
// countryCodes = all countries (mirrors benefits/events country targeting).
export const createRewardSchema = z.object({
  title: z.string().trim().min(2).max(200),
  slug: slugSchema.optional(),
  description: z.string().trim().max(2000),
  imageUrl: urlSchema.nullable().optional(),
  pointsCost: z.number().int().positive(),
  stock: z.number().int().min(0).nullable().optional(),
  validFrom: isoDateTimeSchema.nullable().optional(),
  validUntil: isoDateTimeSchema.nullable().optional(),
  conceptIds: z.array(uuidSchema).optional(),
  countryCodes: z.array(countryCodeSchema).optional(),
});
export type CreateRewardPayload = z.infer<typeof createRewardSchema>;

export const updateRewardSchema = createRewardSchema.partial();
export type UpdateRewardPayload = z.infer<typeof updateRewardSchema>;

// Lifecycle action on a reward (draft↔published↔archived), never the create payload.
export const rewardStatusActionSchema = z.object({ action: z.enum(['publish', 'archive', 'restore']) });
export type RewardStatusAction = z.infer<typeof rewardStatusActionSchema>;

export const rewardListQuerySchema = paginationQuerySchema.extend({
  status: rewardStatusEnum.optional(),
  search: z.string().trim().max(100).optional(),
});
export type RewardListQuery = z.infer<typeof rewardListQuerySchema>;

// Member redeems a reward (points come from the garage's pooled, unlocked balance).
export const createRedemptionSchema = z.object({ rewardId: uuidSchema });
export type CreateRedemptionPayload = z.infer<typeof createRedemptionSchema>;

// Platform admin redemptions queue. Search spans the joined reward title, garage
// name and member name/email (the row has no searchable column of its own).
export const redemptionListQuerySchema = paginationQuerySchema.extend({
  status: redemptionStatusEnum.optional(),
  search: z.string().trim().max(100).optional(),
});
export type RedemptionListQuery = z.infer<typeof redemptionListQuerySchema>;
